import { Group, Stack, Text } from '@mantine/core';
import { DataTable, type DataTableColumn, type DataTableSortStatus } from 'mantine-datatable';
import { StatusBadge } from './status-badge';
import { UserAvatar } from './user-avatar';
import { PAGE_SIZE } from '../magic-values';
import type { AdminUserDoc } from '../types/user';

interface UsersTableProps {
  users: AdminUserDoc[];
  fetching?: boolean;
  onRowClick?: (user: AdminUserDoc) => void;
  showAccount?: boolean;
  page: number;
  totalRecords: number;
  onPageChange: (page: number) => void;
  sortStatus: DataTableSortStatus;
  onSortStatusChange: (status: DataTableSortStatus) => void;
  height?: string | number;
}

function formatDate(value: string | undefined): string {
  return value ? new Date(value).toLocaleString() : '—';
}

const ALL_COLUMNS: DataTableColumn<AdminUserDoc>[] = [
  {
    accessor: 'email',
    title: 'Email',
    sortable: true,
    render: (user) => (
      <Group spacing="sm" noWrap>
        <UserAvatar user={user} size={32} />
        <Stack spacing={0}>
          <Text size="sm">{user.email}</Text>
          <Text size="xs" color="dimmed">
            {user._id}
          </Text>
        </Stack>
      </Group>
    ),
  },
  {
    accessor: 'name',
    title: 'Name',
    sortable: true,
    render: (user) => user.name ?? '—',
  },
  {
    accessor: 'accountId',
    title: 'Account',
    render: (user) =>
      user.accountId ? (
        <Text size="xs" color="dimmed" sx={{ fontFamily: 'monospace' }}>
          {user.accountId}
        </Text>
      ) : (
        '—'
      ),
  },
  {
    accessor: 'role',
    title: 'Role',
    sortable: true,
    render: (user) => user.role ?? '—',
  },
  {
    accessor: 'status',
    title: 'Status',
    sortable: true,
    render: (user) => <StatusBadge status={user.status} />,
  },
  {
    accessor: 'createdAt',
    title: 'Created',
    sortable: true,
    render: (user) => formatDate(user.createdAt),
  },
];

export function UsersTable({
  users,
  fetching,
  onRowClick,
  showAccount = true,
  page,
  totalRecords,
  onPageChange,
  sortStatus,
  onSortStatusChange,
  height = 'calc(100vh - 300px)',
}: UsersTableProps) {
  const columns = showAccount
    ? ALL_COLUMNS
    : ALL_COLUMNS.filter((column) => column.accessor !== 'accountId');

  return (
    <DataTable
      striped
      highlightOnHover
      height={height}
      sx={{ minWidth: 900 }}
      records={users}
      idAccessor="_id"
      fetching={fetching}
      noRecordsText="No users."
      columns={columns}
      onRowClick={onRowClick ? (user) => onRowClick(user) : undefined}
      totalRecords={totalRecords}
      recordsPerPage={PAGE_SIZE}
      page={page}
      onPageChange={onPageChange}
      sortStatus={sortStatus}
      onSortStatusChange={onSortStatusChange}
    />
  );
}
